import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Transition } from "@headlessui/react";
import {
  ChevronRightIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  ChatBubbleBottomCenterTextIcon,
} from "@heroicons/react/24/outline";
import { PMPagination } from "../../utils/PMPagination";
import {
  dateToString,
  InitialsTag,
  StringLimit,
  NewLineFormatter,
} from "../../Utils";
import { CaseStatus, CasePriorityStatus, caseStatusOptions } from "./utils";
import { priorityOptions } from "../quality/deviations/utils";
import { InlineTablePicker } from "../../utils/InlineTablePicker";
import CaseUpdateComposer from "./CaseUpdateComposer";
import { updateCaseAsync } from "../../../slices/case";
import { createCaseItem } from "../../../api/case";
import toast from "react-hot-toast";

/** List index: avoid `items` — each row would load every case item + heavy Item#as_json. Slide panel uses `last_item` only. */
const LIST_FIELDS =
  "id,case_number,title,status,due_date,case_type,user_assigned,customer,created_at,priority,last_item,tag_definitions";

export const CasesTableNewView = ({
  itemState,
  onPageChange,
  onLoadMore,
  itemsCount,
  totalCount,
  currentCount,
  page,
  perPage,
  onRefresh,
  controller,
  onControllerChange,
  modernView = false,
}) => {
  const { data, isLoading } = itemState;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedCase, setSelectedCase] = useState(null);
  const [slidePanelOpen, setSlidePanelOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { caseId, field }

  const handleRowClick = (caseItem) => {
    setSelectedCase(caseItem);
    setSlidePanelOpen(true);
  };

  const handleClosePanel = () => {
    setSlidePanelOpen(false);
    setSelectedCase(null);
  };

  const handleAddUpdate = async (payload) => {
    if (!payload || !selectedCase?.id) return;
    try {
      const response = await createCaseItem(payload);
      const item = response?.data || response;
      if (item?.id) {
        toast.success(t("Update added"));
        onRefresh?.();
        setSelectedCase((prev) =>
          prev
            ? {
                ...prev,
                last_item: {
                  id: item.id,
                  description: payload.description,
                  created_at: item.created_at,
                  title: item.title,
                  action: item.action,
                },
              }
            : null
        );
      } else {
        toast.error(item?.errors?.[0] || t("Could not add update"));
        throw new Error(item?.errors?.[0]);
      }
    } catch (err) {
      toast.error(t("Could not add update"));
      throw err;
    }
  };

  const handleOpenFullCase = () => {
    if (selectedCase?.id) navigate(`/dashboard/cases/${selectedCase.id}`);
  };

  const handleSort = (column) => {
    if (!onControllerChange || !controller) return;
    const currentSortBy = controller.sortBy || "created_at";
    const currentSort = controller.sort || "desc";
    const newSort =
      currentSortBy === column
        ? currentSort.toLowerCase() === "desc"
          ? "asc"
          : "desc"
        : "desc";
    onControllerChange({
      ...controller,
      sortBy: column,
      sort: newSort,
      page: 1,
    });
  };

  const SortIcon = ({ column }) => {
    const isActive = controller?.sortBy === column;
    const isAsc = controller?.sort?.toLowerCase() === "asc";
    if (!isActive) return null;
    return isAsc ? (
      <ChevronUpIcon className="h-4 w-4 text-gray-500" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-gray-500" />
    );
  };

  const handleStatusChange = async (caseItem, newStatus) => {
    setEditingCell(null);
    try {
      const payload = { ...caseItem, status: newStatus };
      const result = await dispatch(updateCaseAsync(payload)).unwrap();
      if (result) {
        toast.success(t("Status updated"));
        onRefresh?.();
      }
    } catch (err) {
      toast.error(t("Could not update status"));
    }
  };

  const handleDueDateChange = async (caseItem, newDate) => {
    setEditingCell(null);
    const dueDate = newDate || null;
    try {
      const payload = { ...caseItem, due_date: dueDate };
      const result = await dispatch(updateCaseAsync(payload)).unwrap();
      if (result) {
        toast.success(t("Due date updated"));
        onRefresh?.();
      }
    } catch (err) {
      toast.error(t("Could not update due date"));
    }
  };

  const handlePriorityChange = async (caseItem, newPriority) => {
    setEditingCell(null);
    const priority = newPriority ? parseInt(newPriority, 10) : 1;
    try {
      const payload = { ...caseItem, priority };
      const result = await dispatch(updateCaseAsync(payload)).unwrap();
      if (result) {
        toast.success(t("Priority updated"));
        onRefresh?.();
      }
    } catch (err) {
      toast.error(t("Could not update priority"));
    }
  };

  return (
    <div className="flow-root">
      {!modernView && (
        <PMPagination
          disabled={isLoading}
          onPageChange={onPageChange}
          page={page}
          perPage={perPage}
          rowsCount={itemsCount}
          totalCount={totalCount}
          currentCount={currentCount}
          showCount={true}
          showPages={true}
          showNextPrev={true}
        />
      )}

      {/* Compact Table - Notion/GitHub style */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  #
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {t("Title")}
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {t("Customer")}
                </th>
                <th
                  scope="col"
                  className="cursor-pointer px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100"
                  onClick={() => handleSort("created_at")}
                >
                  <span className="flex items-center gap-1">
                    {t("Created")}
                    <SortIcon column="created_at" />
                  </span>
                </th>
                <th
                  scope="col"
                  className="cursor-pointer px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100"
                  onClick={() => handleSort("due_date")}
                >
                  <span className="flex items-center gap-1">
                    {t("Due date")}
                    <SortIcon column="due_date" />
                  </span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {t("Status")}
                </th>
                <th
                  scope="col"
                  className="cursor-pointer px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100"
                  onClick={() => handleSort("priority")}
                >
                  <span className="flex items-center gap-1">
                    {t("Priority")}
                    <SortIcon column="priority" />
                  </span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {t("Assigned")}
                </th>
                <th scope="col" className="relative px-3 py-2.5">
                  <span className="sr-only">{t("Actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data?.map((caseItem) => (
                <tr
                  key={caseItem.id}
                  onClick={() => handleRowClick(caseItem)}
                  className="group cursor-pointer transition-colors hover:bg-gray-50"
                >
                  <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-blue-600">
                    {caseItem.case_number}
                  </td>
                  <td className="max-w-[200px] px-3 py-2">
                    <StringLimit
                      text={caseItem.title || t("No title")}
                      limit={40}
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600">
                    {caseItem.customer?.displayTitle || "–"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600">
                    {dateToString(caseItem.created_at)}
                  </td>
                  <td
                    className="whitespace-nowrap px-3 py-2 text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {editingCell?.caseId === caseItem.id &&
                    editingCell?.field === "due_date" ? (
                      <div className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 shadow-sm ring-1 ring-gray-900/5">
                        <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
                        <input
                          type="date"
                          defaultValue={
                            caseItem.due_date
                              ? caseItem.due_date.split("T")[0]
                              : ""
                          }
                          autoFocus
                          onBlur={(e) => {
                            const val = e.target.value;
                            handleDueDateChange(caseItem, val || null);
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleDueDateChange(caseItem, e.target.value || null);
                              setEditingCell(null);
                            }
                            if (e.key === "Escape") setEditingCell(null);
                          }}
                          className="border-0 bg-transparent p-0 text-sm text-gray-700 focus:outline-none focus:ring-0"
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          setEditingCell({ caseId: caseItem.id, field: "due_date" })
                        }
                        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-left text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                      >
                        <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                        {caseItem.due_date
                          ? dateToString(caseItem.due_date)
                          : "–"}
                      </button>
                    )}
                  </td>
                  <td
                    className="px-3 py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {editingCell?.caseId === caseItem.id &&
                    editingCell?.field === "status" ? (
                      <InlineTablePicker
                        options={caseStatusOptions}
                        value={caseItem.status}
                        onChange={(val) => handleStatusChange(caseItem, val)}
                        onClose={() => setEditingCell(null)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          setEditingCell({ caseId: caseItem.id, field: "status" })
                        }
                        className="block"
                      >
                        <CaseStatus caseItem={caseItem} />
                      </button>
                    )}
                  </td>
                  <td
                    className="px-3 py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {editingCell?.caseId === caseItem.id &&
                    editingCell?.field === "priority" ? (
                      <InlineTablePicker
                        options={priorityOptions}
                        value={caseItem.priority?.toString() || "1"}
                        onChange={(val) => handlePriorityChange(caseItem, val)}
                        onClose={() => setEditingCell(null)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          setEditingCell({ caseId: caseItem.id, field: "priority" })
                        }
                        className="block"
                      >
                        <CasePriorityStatus priority={caseItem?.priority} />
                      </button>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {caseItem.user_assigned?.name ? (
                      <InitialsTag name={caseItem.user_assigned.name} />
                    ) : (
                      "–"
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PMPagination
        disabled={isLoading}
        onPageChange={onPageChange}
        onLoadMore={onLoadMore}
        page={page}
        perPage={perPage}
        rowsCount={itemsCount}
        totalCount={totalCount}
        currentCount={currentCount}
        showCount={!modernView}
        showPages={!modernView}
        showNextPrev={!modernView}
        modernView={modernView}
      />

      {/* Right-side Slide Panel - Accordion style from right */}
      {slidePanelOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20"
          onClick={handleClosePanel}
          onKeyDown={(e) => e.key === "Escape" && handleClosePanel()}
          role="button"
          tabIndex={0}
          aria-label="Close panel"
        />
      )}
      <Transition
        show={slidePanelOpen}
        enter="transition-transform duration-300 ease-out"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition-transform duration-200 ease-in"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed top-16 bottom-0 right-0 z-40 w-full max-w-md bg-white shadow-xl pt-4">
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedCase?.case_number || ""}
              </h3>
              <button
                type="button"
                onClick={handleClosePanel}
                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {selectedCase && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs font-medium uppercase text-gray-500">
                      {t("Title")}
                    </h4>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedCase.title || t("No title")}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium uppercase text-gray-500">
                      {t("Status")}
                    </h4>
                    <div className="mt-1">
                      <CaseStatus caseItem={selectedCase} />
                      <CasePriorityStatus priority={selectedCase?.priority} />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium uppercase text-gray-500">
                      {t("Due date")}
                    </h4>
                    <p className="mt-1 text-sm text-gray-600">
                      {selectedCase.due_date
                        ? dateToString(selectedCase.due_date)
                        : "–"}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium uppercase text-gray-500">
                      {t("Customer")}
                    </h4>
                    <p className="mt-1 text-sm text-gray-600">
                      {selectedCase.customer?.displayTitle || "–"}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium uppercase text-gray-500">
                      {t("Case type")}
                    </h4>
                    <p className="mt-1 text-sm text-gray-600">
                      {selectedCase.case_type?.title || "–"}
                    </p>
                  </div>
                  {selectedCase?.tag_definitions?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium uppercase text-gray-500">
                        {t("Labels")}
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {selectedCase.tag_definitions.map((tag) => (
                          <span
                            key={tag.id}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                            style={
                              tag.color
                                ? { backgroundColor: tag.color + "30", color: tag.color }
                                : { backgroundColor: "#f3f4f6", color: "#374151" }
                            }
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <h4 className="flex items-center gap-1.5 text-xs font-medium uppercase text-gray-500">
                      <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />
                      {t("Last update")}
                    </h4>
                    {(() => {
                      const latestItem = selectedCase?.last_item;
                      const hasUpdate = latestItem?.description;
                      return hasUpdate ? (
                        <div className="mt-1 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                          {latestItem?.module === "description" && latestItem?.title?.trim() && (
                            <p className="font-semibold text-gray-900 mb-1 text-sm">{latestItem.title}</p>
                          )}
                          <div className="htmlContent text-sm text-gray-700 [&_.mention-inline]:bg-blue-50 [&_.mention-inline]:text-blue-700 [&_.mention-inline]:rounded [&_.mention-inline]:px-0.5">
                            <NewLineFormatter value={latestItem.description} />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {dateToString(latestItem.created_at)}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-1 text-sm italic text-gray-500">
                          {t("No updates yet")}
                        </p>
                      );
                    })()}
                  </div>
                  <div>
                    <h4 className="mb-2 text-xs font-medium uppercase text-gray-500">
                      {t("Add update")}
                    </h4>
                    <CaseUpdateComposer
                      caseId={selectedCase.id}
                      onSubmit={handleAddUpdate}
                      compact
                      customer={selectedCase?.customer}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 p-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleOpenFullCase}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  {t("Open case")}
                </button>
                <Link
                  to={`/dashboard/cases/${selectedCase?.id}`}
                  className="flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t("View full")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Transition>

    </div>
  );
};

CasesTableNewView.defaultProps = {
  page: 1,
  perPage: 25,
  itemState: { data: [] },
  itemsCount: 0,
  totalCount: 0,
  currentCount: 0,
};

export { LIST_FIELDS };
